import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePlanWords } from '../../utils/planWords.js';
import { editProjectTask, fetchGapRun, fetchNeeds, fetchProjectMap, fetchTaskMd, resolvePlanProposal, setProjectKind, setProjectSchedule, startGapRun } from '@stepflow/ui-shared/services/dataPlaneApi';
import TimelineView from './TimelineView.jsx';
import GapsView from './GapsView.jsx';
import { listPoolMembers } from '@stepflow/ui-shared/services/poolMembersApi.js';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import ProjectMapGraph from './ProjectMapGraph.jsx';
import { buildProjectLanes, clampStart, hasGraphContent, milestonesInWindow, PAGE_STEP, VISIBLE_LANES } from './projectMapGraph.js';

// PROVENANCE (PROJECT-MAP.md §prov) — the compact lazy-info affordance on every epic/task line:
// an icon-only trigger that opens a small popover with WHO updated / authored / approved this item,
// WHEN, and the row's REVISION. Zero extra fetch (the fields ride the map payload); the popover only
// mounts on open (PortalMenu → portal, no clipping, closes on outside-click / scroll / resize).
// SCOPE HONESTY (§prov 100%-accuracy): "Updated / rev" are ROW-level facts; "Approved" is a
// MILESTONE-level fact — it is labeled "Milestone approved" so sibling tasks showing the same
// approver never read as a per-task inconsistency (the mixed-scope phantom).
export function _fmtTime(iso) {
  if (!iso) return '';
  try { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
}
const _AUTOMATED = new Set(['planner', 'reducer', 'integrator', 'reconciler', 'system', 'approve',
  'planner-ui', 'planner:prune', 'concretion', 'final_reducer']);
const _SUB_RE = /^[0-9a-f][0-9a-f-]{18,}$/i;   // a raw Cognito sub (uuid-ish) — never show it whole
export function _actorLabel(a, resolveUser) {
  const s = String(a || '').trim();
  if (!s) return '—';
  if (_AUTOMATED.has(s.toLowerCase())) return `automated · ${s}`;
  // Map a raw sub → the member's workspace name/email (same roster join ExecutionsTab uses).
  const resolved = typeof resolveUser === 'function' ? resolveUser(s) : null;
  if (resolved) return resolved;
  return _SUB_RE.test(s) ? `${s.slice(0, 8)}…` : s;   // unknown sub → shortened, never a bare GUID
}
function _ProvLine({ label, value, sub }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-20 shrink-0 text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-slate-700" title={value}>{value}</span>
        {sub ? <span className="block text-[10px] text-slate-400">{sub}</span> : null}
      </span>
    </div>
  );
}
export function ProvenanceBadge({ prov, resolveUser }) {
  const w = usePlanWords();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  if (!prov) return null;
  // "Run" = the SFN execution that produced this line's current state: the run that EXECUTED the task
  // if it ran (ranById), else the plan/decompose run that AUTHORED it (authoredRunId). Only truly
  // blank for a legacy row seeded before run-id provenance.
  const executed = !!prov.ranById;
  const runLine = prov.ranById || prov.authoredRunId || '(no run recorded)';
  const rev = Number(prov.rev || 0);
  const updatedSub = [_fmtTime(prov.updatedAt), rev > 0 ? `rev ${rev}` : ''].filter(Boolean).join(' · ');
  return (
    <>
      <span
        ref={ref}
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setOpen((v) => !v); } }}
        className="ml-1 shrink-0 cursor-pointer select-none text-[11px] leading-none text-slate-300 hover:text-slate-500"
        title="Who updated / authored / approved this"
        aria-label="Provenance"
        data-testid="pm-prov-toggle"
      >
        ⓘ
      </span>
      <PortalMenu anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          className="w-64 space-y-1.5 rounded-md border border-slate-200 bg-white p-2 shadow-lg"
          data-testid="pm-prov-popover"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ROW-level: last writer + time + revision — the always-present accuracy line. */}
          <_ProvLine
            label="Updated by"
            value={_actorLabel(prov.updatedBy, resolveUser)}
            sub={updatedSub}
          />
          <_ProvLine
            label={executed ? 'Ran in run' : 'Authored by run'}
            value={runLine}
            sub={executed ? _fmtTime(prov.ranAt) : ''}
          />
          {executed ? (
            <_ProvLine label="Ran by" value={_actorLabel(prov.ranBy, resolveUser)} />
          ) : null}
          {/* MILESTONE-level (labeled as such — never a per-task fact). */}
          <_ProvLine
            label={w.group}
            value={prov.approvedBy
              ? `approved by ${_actorLabel(prov.approvedBy, resolveUser)}`
              : 'not yet approved'}
            sub={prov.approvedBy ? _fmtTime(prov.approvedAt) : ''}
          />
        </div>
      </PortalMenu>
    </>
  );
}

// REVISION CHIP (§prov) — the per-line edit counter shown INLINE on every task/epic row (not hidden
// in the ⓘ): a compact ``r{n}`` badge so a viewer sees at a glance which lines changed since they
// last looked. ``rev`` is the monotonic Rev the ledger stamps + bumps on every mutation; 0/absent
// (legacy row) renders nothing (never a fake number). Full who/when stays in the ProvenanceBadge.
export function RevChip({ rev }) {
  const n = Number(rev || 0);
  if (!n) return null;
  return (
    <span
      className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-medium tabular-nums text-slate-400"
      title={`Revision ${n} — number of edits to this line`}
      data-testid="rev-chip"
    >
      r{n}
    </span>
  );
}

// PROJECT MAP (PROJECT-MAP.md) — the live single-pane project state: goal → milestone → epic → task,
// each a POINTER with DERIVED status, plus the deterministic gap report (+ optional LLM sufficiency).
// READ-ONLY + ADDITIVE (a new panel; touches no existing control). Derive-on-read: every open reflects
// the current ledger. "Expand epic → tasks" here = expand the epic row to reveal its child tasks (the
// tree IS the expansion view); an optional Refine button emits ``onRefineEpic`` for the parent to wire
// to the groom composer (never required).

const STATUS_TINT = {
  done: 'text-emerald-600', running: 'text-blue-600', ready: 'text-slate-600',
  planned: 'text-slate-400', blocked: 'text-red-600', missing: 'text-amber-600',
  approved: 'text-emerald-600',
};

function StatusDot({ status }) {
  const tint = STATUS_TINT[status] || 'text-slate-400';
  return <span className={`text-[10px] font-mono uppercase ${tint}`} data-testid="pm-status">{status}</span>;
}

function TaskRow({ task, onLoadMd, resolveUser }) {
  const links = task.links || {};
  // CLICK-TO-VIEW (§task-md): click the task LINE to load/unload its plan .md inline (goal · DoD ·
  // ASCII flow · hand-off table — the same file agents read). Lazy: fetched once on first open via
  // ``onLoadMd`` (keyed by the ledger taskId), rendered with the shared MarkdownRenderer.
  const [open, setOpen] = useState(false);
  const [md, setMd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const toggle = async (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && md === null && !loading && onLoadMd) {
      setLoading(true); setErr('');
      try {
        const res = await onLoadMd(task.taskId || task.id);
        setMd(String((res && res.markdown) || '') || '_(no file yet — run the plan to generate it)_');
      } catch (_e) { setErr('Could not load the task file.'); }
      finally { setLoading(false); }
    }
  };
  return (
    <div data-testid="pm-task" data-status={task.status}>
      <div
        role="button"
        tabIndex={0}
        onClick={onLoadMd ? toggle : undefined}
        onKeyDown={onLoadMd ? (e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); } : undefined}
        className={`flex items-center gap-2 px-1.5 py-0.5 text-[11px] ${onLoadMd ? 'cursor-pointer rounded hover:bg-slate-50' : ''}`}
        title={onLoadMd ? (open ? 'Hide task file' : 'View task file — goal, flow, hand-offs') : undefined}
        data-testid={`pm-task-toggle-${task.taskId || task.id}`}
        aria-expanded={onLoadMd ? open : undefined}
      >
        {onLoadMd ? <span className="shrink-0 text-[9px] text-slate-400">{open ? '▾' : '▸'}</span> : null}
        <StatusDot status={task.status} />
        <span className="truncate text-slate-700">{task.title || task.taskId}</span>
        <RevChip rev={(task.prov || {}).rev} />
        {/* READINESS (P3): the gap agent's verdict + estimate — additive, only once a gap run verdicted it. */}
        {task.estimateDays ? (
          <span className="shrink-0 text-[10px] text-slate-400" data-testid="pm-task-estimate" title="Estimated working days">
            {task.estimateDays}d
          </span>
        ) : null}
        {task.readiness && task.readiness.verdict === 'needs-attention' ? (
          <span className="shrink-0 rounded bg-amber-50 px-1 text-[10px] text-amber-700 ring-1 ring-amber-200"
                data-testid="pm-task-attention" title={(task.readiness.reasons || []).join(' · ')}>
            needs attention
          </span>
        ) : null}
        {(links.provides || []).length ? (
          <span className="ml-auto shrink-0 text-[10px] text-indigo-400" title="provides">↗ {links.provides.join(', ')}</span>
        ) : null}
        <span className={(links.provides || []).length ? 'shrink-0' : 'ml-auto shrink-0'}>
          <ProvenanceBadge prov={task.prov} resolveUser={resolveUser} />
        </span>
      </div>
      {open ? (
        <div className="ml-4 mt-1 mb-1 rounded-md border border-indigo-100 bg-indigo-50/30 px-3 py-2" data-testid={`pm-task-md-${task.taskId || task.id}`}>
          {loading ? <div className="text-[11px] text-slate-400">loading…</div>
            : err ? <div className="text-[11px] text-red-500">{err}</div>
              : <MarkdownRenderer content={md || ''} className="text-[12px]" />}
        </div>
      ) : null}
    </div>
  );
}

function EpicRow({ epic, onRefineEpic, onLoadMd, resolveUser }) {
  const [open, setOpen] = useState(false);
  const tasks = epic.tasks || [];
  return (
    <div className="rounded border border-slate-100" data-testid="pm-epic" data-epic={epic.epicId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-slate-50"
        aria-expanded={open}
        data-testid="pm-epic-toggle"
      >
        <span className="text-[10px] text-slate-400">{open ? '▾' : '▸'}</span>
        <StatusDot status={epic.status} />
        <span className="truncate text-xs font-medium text-slate-700">{epic.story || epic.epicId}</span>
        <RevChip rev={(epic.prov || {}).rev} />
        <span className="ml-auto shrink-0 text-[10px] text-slate-400">{tasks.length} task{tasks.length === 1 ? '' : 's'}</span>
        <ProvenanceBadge prov={epic.prov} resolveUser={resolveUser} />
        {onRefineEpic ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onRefineEpic(epic); }}
            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-blue-600 hover:bg-blue-50"
            data-testid="pm-refine-epic"
            title="Break this epic into granular executable tasks (only tasks run)"
          >
            {tasks.length ? 'Add tasks →' : 'Create tasks →'}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="border-t border-slate-100 pl-4">
          {tasks.length ? tasks.map((t) => <TaskRow key={t.id} task={t} onLoadMd={onLoadMd} resolveUser={resolveUser} />)
            : <div className="px-1.5 py-1 text-[11px] italic text-amber-600" data-testid="pm-empty-epic">Not broken down yet — use “Create tasks” (only leaf tasks run).</div>}
        </div>
      ) : null}
    </div>
  );
}


// (P2) The gap agent's report, one line — what its run found and did (the cards below carry the details).
function _gapRunSummary(sm) {
  const suf = sm.sufficiency || {};
  const cr = sm.criteria || {};
  const parts = [
    sm.found ? `${sm.found} broken handoff(s): ${sm.fixed || 0} auto-fixed · ${sm.proposed || 0} suggested · ${sm.open || 0} need a decision`
      : 'handoffs connected',
    suf.missing ? `${suf.missing} missing capabilit${suf.missing === 1 ? 'y' : 'ies'} (${suf.proposed || 0} proposed)` : null,
    cr.missing ? `${cr.missing} milestone(s) without an exit criterion (${cr.proposed || 0} drafted)` : null,
    (sm.readiness && sm.readiness.tasks) ? `${sm.readiness.ready || 0}/${sm.readiness.tasks} tasks ready`
      + (sm.readiness.attention ? ` · ${sm.readiness.attention} need attention` : '') : null,
    // G1: what the plan needs from a human (rules now; the prerequisites agent in G2)
    (sm.prerequisites && (sm.prerequisites.open ?? sm.prerequisites.found))
      ? (() => {
        const o = sm.prerequisites.open ?? sm.prerequisites.found;
        const b = sm.prerequisites.blocking;
        return `${o} thing${o === 1 ? '' : 's'} you need to provide${b ? ` (${b} essential)` : ''}`;
      })() : null,
  ].filter(Boolean);
  return `Gap agent — ${parts.join(' · ')}`;
}

export default function ProjectMapPanel({ networkId, poolId, onRefineEpic, planUpdatedSignal = 0, planUpdating = false, onOpenPlanDoc = null,
  onOpenConnections = null }) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [gapBusy, setGapBusy] = useState(false);
  const _lastSignalRef = React.useRef(planUpdatedSignal);
  // Pool roster (sub → workspace name/email) so provenance shows "Jane Doe", never a raw Cognito
  // UUID — the same join ExecutionsTab's User column uses. Best-effort: a failed members fetch
  // leaves the resolver empty and _actorLabel falls back to a shortened sub.
  const [usersBySub, setUsersBySub] = useState({});
  useEffect(() => {
    if (!networkId || !poolId) { setUsersBySub({}); return undefined; }
    let cancelled = false;
    listPoolMembers(networkId, poolId)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const mem of (res?.members || [])) {
          const sub = String(mem?.userSub || '').trim();
          if (!sub) continue;
          const name = String(mem?.userName || '').trim();
          const email = String(mem?.email || '').trim();
          if (name || email) map[sub] = name || email;
        }
        setUsersBySub(map);
      })
      .catch(() => { if (!cancelled) setUsersBySub({}); });
    return () => { cancelled = true; };
  }, [networkId, poolId]);
  const resolveUser = useCallback((sub) => usersBySub[String(sub || '').trim()] || null, [usersBySub]);

  // COLLAPSE APPROVED MILESTONES by default (parity with the Tasks tab's fold): an approved
  // milestone is a signed-off, immutable slice — collapse it so the map opens on the WORK IN FLIGHT
  // (planning/executing/pending milestones), not a wall of done history. Keyed by milestone id in a
  // Set of collapsed ids; the header chevron toggles. Manual expand/collapse afterwards sticks.
  const [collapsedMs, setCollapsedMs] = useState(() => new Set());
  // TIMELINE | TEXT | GRAPH view switcher (§13 + PLAN-TO-EXECUTE §5): the Timeline is the DEFAULT read
  // (owner 2026-09-29: "when, and in parallel with what"); the text tree and the assembly-line DAG are
  // one click away and unchanged.
  const [mapView, setMapView] = useState('timeline');
  // BOARD WINDOW state lives HERE so the ‹Prev · pills · Next› nav renders in the compact header
  // row (space-saving — every vertical pixel goes to the graph). The graph is canvas-only.
  const board = React.useMemo(() => buildProjectLanes(state.data || {}), [state.data]);
  const [boardStart, setBoardStart] = useState(0);
  useEffect(() => { setBoardStart(board.defaultStart); }, [board.defaultStart, board.lanes.length]);
  const _initialFoldRef = useRef('');
  useEffect(() => {
    const ms = state.data?.milestones;
    if (!Array.isArray(ms) || !ms.length) return;
    if (_initialFoldRef.current === networkId) return;   // fold ONCE per network; user choice sticks
    _initialFoldRef.current = networkId;
    setCollapsedMs(new Set(ms.filter((m) => m.status === 'approved').map((m) => m.id)));
  }, [state.data, networkId]);
  const toggleMs = useCallback((mid) => {
    setCollapsedMs((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  }, []);

  // TIMELINE view mode: dependency-only (parallel work overlaps — the default) or milestone-gated.
  const [gateMs, setGateMs] = useState(false);
  const gateRef = useRef(false);
  const load = useCallback(async (withGap) => {
    if (!networkId) return;
    setState((s) => ({ ...s, loading: !withGap, error: '' }));
    if (withGap) setGapBusy(true);
    try {
      const data = await fetchProjectMap(networkId, { pool: poolId, gap: !!withGap, ...(gateRef.current ? { gate: true } : {}) });
      setState({ loading: false, error: '', data });
    } catch (e) {
      setState({ loading: false, error: (e && e.message) || 'failed to load project map', data: null });
    } finally {
      if (withGap) setGapBusy(false);
    }
  }, [networkId, poolId]);

  useEffect(() => { load(false); }, [load]);

  // GAPS (G3): what the plan needs from a person — loaded beside the map (quiet; a failure leaves the
  // last list) and re-read on every PLAN_UPDATED (answers + gap-agent passes fan it).
  const [needs, setNeeds] = useState([]);
  const loadNeeds = useCallback(async () => {
    if (!networkId) return;
    try {
      const r = await fetchNeeds(networkId, { pool: poolId });
      setNeeds(Array.isArray(r && r.needs) ? r.needs : []);
    } catch (_e) { /* keep the last list */ }
  }, [networkId, poolId]);
  useEffect(() => { loadNeeds(); }, [loadNeeds, planUpdatedSignal]);

  // ONE GAP AGENT (P2): "Run gap analysis" starts the gap agent as a tracked background job and POLLS it —
  // the old path re-ran its analyst synchronously (titles only, request-timeout bound) and acted on nothing.
  const [gapRun, setGapRun] = useState(null);
  const pollRef = useRef(null);
  const stopPoll = useCallback(() => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } }, []);
  const pollGap = useCallback(async () => {
    try {
      const r = await fetchGapRun(networkId, { pool: poolId });
      const job = (r && r.gapRun) || null;
      setGapRun(job);
      if (job && (job.status === 'queued' || job.status === 'running')) {
        pollRef.current = setTimeout(pollGap, 3000);
      } else {
        setGapBusy(false);
        load(false);              // the pass wrote proposals / fixes → re-pull the map
        loadNeeds();              // …and what the plan needs from you (step 5) — the Gaps tab count (live 2026-09-30)
      }
    } catch (e) {
      setGapBusy(false);
    }
  }, [networkId, poolId, load, loadNeeds]);
  const runGap = useCallback(async () => {
    if (!networkId) return;
    setGapBusy(true);
    stopPoll();
    try {
      const r = await startGapRun(networkId, { pool: poolId });
      setGapRun((r && r.gapRun) || null);
      pollRef.current = setTimeout(pollGap, 1500);
    } catch (e) {
      setGapBusy(false);
      setGapRun({ status: 'failed', label: (e && e.message) || 'could not start the gap agent' });
    }
  }, [networkId, poolId, pollGap, stopPoll]);
  useEffect(() => {        // a run already in flight when the map opens → resume showing its progress
    const job = state.data && state.data.gapRun;
    if (job && !gapRun) {
      setGapRun(job);
      if ((job.status === 'queued' || job.status === 'running') && !pollRef.current) {
        setGapBusy(true);
        pollRef.current = setTimeout(pollGap, 3000);
      }
    }
  }, [state.data, gapRun, pollGap]);
  useEffect(() => stopPoll, [stopPoll]);

  // TIMELINE (P4): a drag/resize → the server's conflict-checked schedule action; the returned timeline
  // replaces the map's. A 409 (starts before a producer ends) comes back as {conflict} for "Save anyway".
  const scheduleTask = useCallback(async (taskId, patch) => {
    try {
      const r = await editProjectTask(networkId, taskId, {
        action: 'schedule', ...(poolId ? { poolId } : {}), ...(gateRef.current ? { gate: true } : {}), ...patch });
      if (r && r.timeline) setState((st) => ({ ...st, data: st.data ? { ...st.data, timeline: r.timeline } : st.data }));
      return { ok: true };
    } catch (e) {
      if (e && e.status === 409) return { conflict: true, message: (e.data && e.data.message) || e.message };
      return { conflict: true, message: (e && e.message) || 'could not save' };
    }
  }, [networkId, poolId]);

  // Re-fetch just the timeline (quiet — the rest of the map is unchanged): gate toggle, settings saved.
  const reloadTimeline = useCallback(async () => {
    try {
      const data = await fetchProjectMap(networkId, { pool: poolId, ...(gateRef.current ? { gate: true } : {}) });
      if (data && data.timeline) {
        setState((st) => (st.data ? { ...st, data: { ...st.data, timeline: data.timeline } } : st));
      }
    } catch (_e) { /* keep the current timeline */ }
  }, [networkId, poolId]);
  const toggleGate = useCallback(async (next) => {
    gateRef.current = !!next;
    setGateMs(!!next);
    await reloadTimeline();
  }, [reloadTimeline]);

  // PROJECT KIND (G0) — shared; the server refuses a change once a milestone is locked.
  const [kindBusy, setKindBusy] = useState(false);
  const [kindErr, setKindErr] = useState('');
  const changeKind = useCallback(async (kind) => {
    if (!networkId || !state.data || (state.data.projectKind || 'executable') === kind) return;
    setKindBusy(true);
    setKindErr('');
    try {
      await setProjectKind(networkId, kind, { pool: poolId });
      setState((st) => (st.data ? { ...st, data: { ...st.data, projectKind: kind } } : st));
    } catch (e) {
      setKindErr((e && e.data && e.data.message) || (e && e.message) || 'could not change the project kind');
    } finally {
      setKindBusy(false);
    }
  }, [networkId, poolId, state.data]);

  // TIMELINE SETTINGS — start date · weeks target · milestone days; shared per project. A target changes
  // the whole fit (server-derived), so the timeline is re-fetched after every save.
  const saveSchedule = useCallback(async (patch) => {
    await setProjectSchedule(networkId, patch, { pool: poolId });
    await reloadTimeline();
  }, [networkId, poolId, reloadTimeline]);

  // GAP AGENT proposals (PLAN-INTEGRATION §3.6): Accept / Dismiss ONE verified fix for an approved
  // milestone, then quiet-reload the map. Per-proposal busy state; a stale proposal just disappears.
  const [proposalBusy, setProposalBusy] = useState('');
  const resolveProposal = useCallback(async (gapId, action) => {
    if (!networkId || !gapId) return;
    setProposalBusy(gapId);
    try {
      await resolvePlanProposal(networkId, gapId, action, poolId ? { pool: poolId } : {});
      await load(false);
    } catch (_e) { /* best-effort — the reload reflects whatever landed */ }
    finally { setProposalBusy(''); }
  }, [networkId, poolId, load]);

  // TASK FILE loader (§task-md): lazy per-task .md fetch, keyed by ledger taskId + scoped to the pool.
  const loadTaskMd = useCallback(
    (taskId) => fetchTaskMd(networkId, taskId, poolId ? { pool: poolId } : {}),
    [networkId, poolId],
  );

  // PLAN LIVE (§2.5g): a PLAN_UPDATED beacon bumped ``planUpdatedSignal`` → quiet-refresh the map so it
  // stays in lock-step with the Tasks tab (mirrors TasksTab). Ref-guarded so it fires only on a real
  // bump, not the initial mount (the useEffect above already did the first load).
  useEffect(() => {
    if (planUpdatedSignal && planUpdatedSignal !== _lastSignalRef.current) {
      _lastSignalRef.current = planUpdatedSignal;
      load(false);
    }
  }, [planUpdatedSignal, load]);

  const { loading, error, data } = state;
  if (loading) return <div className="p-3 text-xs text-slate-500" data-testid="pm-loading">Loading project map…</div>;
  if (error) return <div className="p-3 text-xs text-red-600" data-testid="pm-error">{error}</div>;
  if (!data || data.eligible === false) {
    return <div className="p-3 text-xs text-slate-500" data-testid="pm-not-project">Not a project network — no map.</div>;
  }

  const proj = data.project || {};
  const facts = data.gapFacts || {};
  const gap = data.gapAnalysis || null;
  const pct = Math.round((proj.coverage || 0) * 100);
  const graphable = hasGraphContent(data);
  const showGraph = graphable && mapView === 'graph';
  const showTimeline = mapView === 'timeline';
  const showGaps = mapView === 'gaps';
  const activeView = showGaps ? 'gaps' : (showTimeline ? 'timeline' : (showGraph ? 'graph' : 'text'));
  // Gaps (n): open blocking needs + the gap agent's open plan-fix cards
  const planFixList = (data && data.backlog ? data.backlog : []).filter((g) => g.kind === 'proposal');
  const gapsCount = needs.filter((n) => (n.status === 'open' || n.status === 'failing') && n.blocking).length
    + planFixList.length;
  const bStart = clampStart(boardStart, board.lanes.length);
  const loadedMs = milestonesInWindow(board.lanes, bStart);
  const gapDone = gapRun && (gapRun.status === 'done' || gapRun.status === 'failed') ? gapRun : null;
  // DEFERRED gaps (touched an APPROVED milestone; not yet a proposal) — one line in the gap panel. The
  // Backlog panel + "Reconcile plan" were removed (2026-09-29): the gap agent is the one fix path.
  const deferred = (data.backlog || []).filter((g) => g.kind !== 'proposal');
  const hasGapFindings = (facts.blocked || []).length || (facts.emptyEpics || []).length || !!gap || !!gapDone
    || deferred.length;

  return (
    <div className="space-y-3 p-3" data-testid="project-map-panel">
      {/* COMPACT HEADER (space-saving — every vertical pixel goes to the board): ONE row holding
          title · status · coverage · Run-gap · Graph|Text (left) and the board nav ‹Prev · M0…Mn ·
          Next› (right). The old standalone GAPS box + switcher row are folded in here. */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-800">Project map</span>
          <StatusDot status={proj.status} />
          <span className="text-[11px] tabular-nums text-slate-500" data-testid="pm-coverage">
            {proj.taskDone || 0}/{proj.taskTotal || 0} · {pct}%
          </span>
          {planUpdating ? (
            <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600" data-testid="pm-plan-updating">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />plan updating…
            </span>
          ) : null}
          <button
            type="button"
            onClick={runGap}
            disabled={gapBusy}
            className="rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 shadow-sm transition-colors hover:bg-blue-100 disabled:opacity-50"
            data-testid="pm-run-gap"
            title="Runs the gap agent in the background: structural handoffs, missing capabilities, missing exit criteria"
          >
            {gapBusy
              ? `Gap agent · ${gapRun && gapRun.step ? `step ${gapRun.step}/${gapRun.steps || 5} · ` : ''}${(gapRun && gapRun.label) || 'starting'}…`
              : 'Run gap analysis'}
          </button>
          {/* PROJECT KIND (G0) — Executable (agents do the work) | Plan & verify (people do the work,
              tasks complete on verified proof). Shared; frozen once a milestone is locked. */}
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 shadow-sm"
               role="radiogroup" aria-label="Project kind" data-testid="pm-kind"
               title={data.projectKindLocked
                 ? 'The project kind is fixed while a milestone is locked — unlock every milestone to change it'
                 : 'Executable: agents do the work. Plan & verify: people do the work and upload proof; agents check it.'}>
            {[['executable', 'Executable'], ['plan_verify', 'Plan & verify']].map(([id, label], i) => (
              <button key={id} type="button" role="radio" data-testid={`pm-kind-${id}`}
                aria-checked={(data.projectKind || 'executable') === id}
                disabled={!!data.projectKindLocked || kindBusy}
                onClick={() => changeKind(id)}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${i > 0 ? 'border-l border-slate-300' : ''} ${
                  (data.projectKind || 'executable') === id ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'} disabled:opacity-60`}>
                {label}
              </button>
            ))}
          </div>
          {kindErr ? <span className="text-[11px] text-rose-600" data-testid="pm-kind-error">{kindErr}</span> : null}
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 shadow-sm"
               role="tablist" aria-label="Project map view">
            {[['timeline', 'Timeline'], ['text', 'Text'], ['graph', 'Graph'], ['gaps', `Gaps (${gapsCount})`]].map(([id, label], i) => (
              <button
                key={id} type="button" role="tab"
                aria-selected={activeView === id}
                disabled={id === 'graph' && !graphable}
                onClick={() => setMapView(id)}
                data-testid={`pm-view-${id}`}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                  i > 0 ? 'border-l border-slate-300' : ''} ${
                  activeView === id
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {showGraph ? (
            <div className="ml-auto flex items-center gap-1.5" data-testid="pmg-nav">
              <button type="button" data-testid="pmg-prev" disabled={bStart <= 0}
                onClick={() => setBoardStart(clampStart(bStart - PAGE_STEP, board.lanes.length))}
                className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30">
                ‹ Prev
              </button>
              <div className="flex items-center gap-0.5">
                {board.milestones.map((m) => (
                  <button key={m.id} type="button" data-testid={`pmg-ms-${m.id}`}
                    aria-current={loadedMs.has(m.id) ? 'true' : undefined}
                    onClick={() => setBoardStart(clampStart(m.firstLane, board.lanes.length))}
                    title={`${m.name}${m.status === 'approved' ? ' — approved' : ''}`}
                    className={`rounded-md px-1.5 py-1 text-[11px] font-semibold transition-colors ${
                      loadedMs.has(m.id) ? 'bg-slate-800 text-white'
                        : m.status === 'approved' ? 'text-emerald-600 hover:bg-emerald-50'
                          : 'text-slate-500 hover:bg-slate-100'}`}>
                    {m.id}
                  </button>
                ))}
              </div>
              <button type="button" data-testid="pmg-next"
                disabled={bStart + VISIBLE_LANES >= board.lanes.length}
                onClick={() => setBoardStart(clampStart(bStart + PAGE_STEP, board.lanes.length))}
                className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30">
                Next ›
              </button>
            </div>
          ) : null}
        </div>
        {proj.goal ? <div className="mt-0.5 text-[11px] text-slate-500">{proj.goal}</div> : null}
        {/* PLAN REVISION (§prov): the plan-level changelog head — the map is a pure projection of the
            ledger, so this states exactly which revision it derives from + who/when/why last changed
            it. Hidden for a legacy plan no authoring batch has bumped yet. */}
        {data.planRev && data.planRev.rev ? (
          <div className="mt-0.5 text-[10px] text-slate-400" data-testid="pm-plan-rev">
            plan rev {data.planRev.rev}
            {data.planRev.reason ? ` · ${data.planRev.reason}` : ''}
            {' · '}{_actorLabel(data.planRev.updatedBy, resolveUser)}
            {data.planRev.updatedAt ? ` · ${_fmtTime(data.planRev.updatedAt)}` : ''}
          </div>
        ) : null}
        <div className="mt-1 h-1 w-full overflow-hidden rounded bg-slate-100">
          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Gap FINDINGS only (the Run button lives in the header; a clean plan shows nothing —
          space goes to the board). */}
      {hasGapFindings ? (
      <div className="rounded bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600" data-testid="pm-gap">
        <ul className="space-y-0.5">
          {(facts.blocked || []).length ? <li data-testid="pm-gap-blocked">{facts.blocked.length} blocked (unmet deps)</li> : null}
          {(facts.emptyEpics || []).length ? <li data-testid="pm-gap-empty">{facts.emptyEpics.length} empty epic(s)</li> : null}
          {deferred.length ? (
            <li data-testid="pm-gap-deferred" className="truncate"
                title={deferred.map((g) => `${g.milestone}: ${g.key || g.kind}${g.detail ? ` — ${g.detail}` : ''}`).join('\n')}>
              {deferred.length} deferred gap{deferred.length === 1 ? '' : 's'} in approved milestones —{' '}
              {deferred.slice(0, 3).map((g) => `${g.milestone} ${g.key || g.kind}`).join(' · ')}{deferred.length > 3 ? ' …' : ''}
            </li>
          ) : null}
          {gapDone ? <li data-testid="pm-gap-run" className={gapDone.status === 'failed' ? 'text-red-600' : 'text-slate-600'}>
            {gapDone.status === 'failed'
              ? `Gap agent failed — ${gapDone.error || gapDone.label || 'unknown error'}`
              : _gapRunSummary(gapDone.summary || {})}
          </li> : null}
          {gap && (gap.missing || []).length ? gap.missing.map((m, i) => (
            <li key={i} className="text-amber-700" data-testid="pm-gap-missing">missing — {m.node}: {m.why}</li>
          )) : null}
          {gap && (gap.advisories || []).map((a, i) => (
            <li key={`a${i}`} className="text-amber-600" data-testid="pm-gap-advisory">{a}</li>
          ))}
        </ul>
      </div>
      ) : null}

      {/* PROJECT BOARD (§13) — canvas only; the Graph|Text switcher + board nav live in the
          compact header row above. Click a card → Text view at that milestone. */}
      {showGraph ? (
        <ProjectMapGraph
          board={board}
          start={bStart}
          onLoadTaskMd={loadTaskMd}
          onOpenPlanDoc={onOpenPlanDoc}
        />
      ) : null}

      {/* GAPS (G3) — one pane: needs from you · plan fixes (the gap agent's cards, moved here) · agent
          questions · broken connections. */}
      {showGaps ? (
        <GapsView networkId={networkId} poolId={poolId} needs={needs} onChanged={loadNeeds}
          onOpenConnections={onOpenConnections} planFixCount={planFixList.length}
          planFixes={planFixList.length ? (
            <div className="space-y-1" data-testid="pm-proposals">
              {(data.backlog || []).filter((g) => g.kind === 'proposal').map((g) => (
                <div key={g.gapId} className="flex items-start gap-2 rounded border border-emerald-100 bg-emerald-50/50 px-2 py-1.5 text-[11px] text-slate-700" data-testid="pm-proposal">
                  <span className="shrink-0 font-mono text-[10px] text-emerald-600">{g.milestone}</span>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-emerald-700">
                      {g.proposal && g.proposal.type === 'exit-criterion' ? 'Exit criterion'
                        : g.proposal && g.proposal.type === 'sufficiency' ? 'Missing capability' : 'Suggested fix'}
                    </span>
                    <span className="ml-1 text-slate-600">{g.summary || g.detail}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => resolveProposal(g.gapId, 'accept')}
                    disabled={proposalBusy === g.gapId}
                    className="shrink-0 px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="pm-proposal-accept"
                    title={g.proposal && g.proposal.type === 'exit-criterion'
                      ? "Adopt this as the milestone's exit criterion (the gap agent drafted it from the goal + the milestone's tasks)"
                      : 'Apply this verified fix to the approved milestone (it stays approved)'}
                  >
                    {proposalBusy === g.gapId ? 'Applying…' : 'Accept'}
                  </button>
                  <button
                    type="button"
                    onClick={() => resolveProposal(g.gapId, 'dismiss')}
                    disabled={proposalBusy === g.gapId}
                    className="shrink-0 px-2.5 py-1 text-xs font-medium rounded-md transition-colors border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="pm-proposal-dismiss"
                  >
                    Dismiss
                  </button>
                </div>
              ))}
            </div>
          ) : null} />
      ) : null}

      {/* The tree: milestone → epic → task */}
      {showTimeline ? (
        <TimelineView timeline={data.timeline} onSchedule={scheduleTask} onSaveSettings={saveSchedule}
          onOpenTask={onOpenPlanDoc} gateMilestones={gateMs} onToggleGate={toggleGate} />
      ) : null}
      {showGraph || showTimeline || showGaps ? null : (
      <div className="space-y-2">
        {(data.milestones || []).map((m) => {
          const collapsed = collapsedMs.has(m.id);
          const epicCount = (m.epics || []).length;
          const taskCount = (m.epics || []).reduce((n, e) => n + (e.tasks || []).length, 0);
          return (
          <div key={m.id} data-testid="pm-milestone" data-milestone={m.id}>
            <button
              type="button"
              onClick={() => toggleMs(m.id)}
              className="flex w-full items-center gap-2 px-1 py-0.5 text-left hover:bg-slate-50"
              aria-expanded={!collapsed}
              data-testid="pm-milestone-toggle"
            >
              <span className="text-[10px] text-slate-400">{collapsed ? '▸' : '▾'}</span>
              <span className="text-[11px] font-semibold text-slate-700">{m.id} {m.name}</span>
              <StatusDot status={m.status} />
              {/* PLAN-DRIFT: the "N break(s)" badge was removed — drift is fixed/aligned/backlogged,
                  never shown as a per-milestone warning. */}
              {/* Collapsed summary so an approved/folded milestone still reads at a glance. */}
              {collapsed ? (
                <span className="ml-auto shrink-0 text-[10px] text-slate-400" data-testid="pm-milestone-fold-summary">
                  {epicCount} epic{epicCount === 1 ? '' : 's'} · {taskCount} task{taskCount === 1 ? '' : 's'}
                  {m.status === 'approved' ? ' · approved' : ''}
                </span>
              ) : null}
            </button>
            {collapsed ? null : (
              <div className="ml-2 space-y-1">
                {(m.epics || []).map((e) => (
                  <EpicRow
                    key={e.id}
                    epic={e}
                    // Bind the milestone context so the parent can build a complete epic groomScope
                    // (milestone id+name) without re-deriving it from the "M/E" composite id.
                    onRefineEpic={onRefineEpic
                      ? (ep) => onRefineEpic({ ...ep, milestoneId: m.id, milestoneName: m.name })
                      : undefined}
                    onLoadMd={loadTaskMd}
                    resolveUser={resolveUser}
                  />
                ))}
              </div>
            )}
          </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
